import {
  CaretRightOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import {
  Button,
  List,
  Pagination,
  Popconfirm,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import { useState } from 'react'
import VSpace from '../../../common/VSpace'
import LaunchPresetModal from './LaunchPresetModal'
import PresetEditorModal from './PresetEditorModal'
import {
  EvalRunPreset,
  formatSelectionSummary,
  PRESETS_PAGE_SIZE,
} from './presetsAdminModel'
import {
  useDeletePresetMutation,
  usePresetsListQuery,
} from './presetsAdminQueries'

const PresetsExplorer = () => {
  const [page, setPage] = useState(1)
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<EvalRunPreset | null>(null)
  const [launching, setLaunching] = useState<EvalRunPreset | null>(null)

  const filters = { page, pageSize: PRESETS_PAGE_SIZE }
  const { data, isLoading } = usePresetsListQuery(filters)
  const deleteMutation = useDeletePresetMutation()

  const presets = data?.presets ?? []
  const total = data?.total ?? 0

  const renderItem = (preset: EvalRunPreset) => (
    <List.Item
      actions={[
        <Tooltip key="launch" title="Launch run">
          <Button
            type="text"
            icon={<CaretRightOutlined />}
            onClick={() => setLaunching(preset)}
          />
        </Tooltip>,
        <Tooltip key="edit" title="Edit preset">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => setEditing(preset)}
          />
        </Tooltip>,
        <Tooltip key="delete" title="Delete preset">
          <Popconfirm
            title="Delete this preset?"
            description="Runs already launched from it are unaffected."
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => deleteMutation.mutate(preset.preset_id)}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Tooltip>,
      ]}
    >
      <List.Item.Meta
        title={<Typography.Text strong>{preset.name}</Typography.Text>}
        description={
          <Typography.Text type="secondary">
            {formatSelectionSummary(preset)} · {preset.trials} trial(s)
          </Typography.Text>
        }
      />
      {preset.include_mutants && <Tag color="purple">mutants</Tag>}
    </List.Item>
  )

  return (
    <VSpace>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography.Title level={4} style={{ marginBottom: 0 }}>
          Presets
        </Typography.Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setCreating(true)}
        >
          New preset
        </Button>
      </div>
      <List
        bordered
        loading={isLoading}
        dataSource={presets}
        locale={{ emptyText: 'No presets' }}
        renderItem={renderItem}
      />
      {total > 0 && (
        <Pagination
          current={page}
          pageSize={PRESETS_PAGE_SIZE}
          total={total}
          onChange={setPage}
          showSizeChanger={false}
          hideOnSinglePage
          showTotal={(t) => `${t} preset(s)`}
          style={{ textAlign: 'right' }}
        />
      )}
      {creating && (
        <PresetEditorModal open onClose={() => setCreating(false)} />
      )}
      {editing && (
        <PresetEditorModal
          open
          preset={editing}
          onClose={() => setEditing(null)}
        />
      )}
      {launching && (
        <LaunchPresetModal
          open
          preset={launching}
          onClose={() => setLaunching(null)}
        />
      )}
    </VSpace>
  )
}

export default PresetsExplorer
